import {
  getAuth,
  signInWithEmailAndPassword,
  fetchSignInMethodsForEmail,
  sendEmailVerification,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  signInWithPopup,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  linkWithPhoneNumber,
} from "firebase/auth";
import { auth, provider } from "../config/firebase";
import {
  loginUser,
  registerUser,
  generateOTPForUser,
  verifyOTPForUser,
} from "./auth";
import { postCall } from "./api";
import { getUrlParameter } from "../utils/getUrlParameter";

export let firebase_token;

/**
 * The Firebase ID token for a signed-in user.
 *
 * `user.accessToken` is an internal field on the User object, not part of the
 * SDK's surface. It is usually populated, which is why it worked — but it is
 * not refreshed when it expires and is absent on some paths, and reading it
 * silently yielded `undefined`. The request then went out with
 * `firebaseToken: undefined` and came back 400 Validation Failed, which reads
 * like the server rejecting a login rather than the client never sending one.
 *
 * getIdToken() is the documented call and refreshes an expired token. This
 * wrapper additionally refuses to return anything that is not a non-empty
 * string, so a missing token fails here with a clear message instead of
 * becoming a malformed request.
 */
const idTokenOf = async (user) => {
  const token = await user?.getIdToken?.();
  if (typeof token !== "string" || !token) {
    throw new Error("Could not read your sign-in token. Try signing in again.");
  }
  return token;
};


const linkFirebase = async (token, userId, verificationToken) => {
  // No `firebase_token` in bodyParams. postCall already puts the token in the
  // body as `firebaseToken` from its own argument, so passing it again under a
  // second name sent both — and linkFirebaseUserValidation is a plain
  // Joi.object, which refuses unknown keys. Every activation was rejected as
  // "Validation Failed" in a few milliseconds, before the controller ran.
  return await postCall({
    firebaseToken: token,
    path: "/auth/link-firebase",
    bodyParams: { userId, verificationToken },
  });
};

export const verifyOTP = async (otp) => {
  try {
    const authUser = getAuth();
    const token = await authUser?.currentUser?.getIdToken();
    if (token) {
      const res = await verifyOTPForUser(token, otp);
      return res;
    } else {
      return { success: false, message: "re-login to recover account" };
    }
  } catch (error) {
    console.log(error);
    return { success: false, data: { message: error.message } };
  }
};

export const generateOTPWithFirebaseId = async () => {
  try {
    const authUser = getAuth();
    const token = await authUser?.currentUser?.getIdToken();
    if (token) {
      const res = await generateOTPForUser(token);
      return res;
    } else {
      return { success: false, message: "re-login to recover account" };
    }
  } catch (error) {
    console.log(error);
    return { success: false, data: { message: error.message } };
  }
};

export const sendReverificationEmail = async () => {
  try {
    const authUser = getAuth();
    await sendEmailVerification(authUser.currentUser);
    return { success: true };
  } catch (error) {
    console.log(error);
    return { success: false, data: { message: error.message } };
  }
};

export const signupWithEmailAndPassword = async (input) => {
  const { email, password } = input;
  let user;
  let token;
  let response;
  try {
    const userCreated = await createUserWithEmailAndPassword(
      auth,
      email,
      password
    );
    user = userCreated.user;
    token = await user.getIdToken();

    if (auth?.currentUser && user) {
      const { fname, lname, email, phone } = input;
      const userInfo = { fname, lname, email, phone };
      response = await registerUser(token, userInfo);
      if (response && response.success) {
        // The account exists in Firebase and in our database by this line.
        // Sending the verification email is a follow-up, and Firebase rate-limits
        // it hard — so letting a failure here fall into the catch below reported
        // a finished signup as failed, and the retry then failed with
        // email-already-in-use, leaving somebody holding an account they had
        // just been told they did not have.
        let verificationSent = true;
        let verificationError;
        try {
          await sendEmailVerification(auth.currentUser);
        } catch (err) {
          verificationSent = false;
          verificationError = err?.code || err?.message;
          console.warn("[signup] account created, verification email failed:", verificationError);
        }
        return {
          success: true,
          data: response,
          verificationSent,
          verificationError,
          message: verificationSent
            ? "sent verification email"
            : "account created, verification email not sent",
        };
      }
      return response;
    }
    return response;
  } catch (error) {
    console.log(error);
    const { message = "" } = error;
    if (message.includes("email-already-in-use")) {
      const auth = getAuth();
      if (auth?.currentUser) {
        const token = await auth?.currentUser?.getIdToken();
        const { fname, lname, email, phone } = input;
        const userInfo = { fname, lname, email, phone };
        const response = await registerUser(token, userInfo);
        if (response && response.success) {
          const sentEmail = await sendEmailVerification(auth.currentUser);
          return {
            success: true,
            data: response,
            message: "sent verification email",
          };
        }
        return response;
      }
    }
    return { success: false, data: { message: error.message } };
  }
};

// Employee invite activation: create the Firebase auth account with the chosen
// password, then link it to the pre-created User via the verification token.
// Handles the case where a Firebase account for this email already exists (e.g.
// a re-attempt) by signing in instead of re-creating.
/**
 * Firebase account errors during activation, in words a person can act on.
 *
 * Same job as phoneAuthMessage below: the raw messages carry the code in
 * parentheses and read as an app failure rather than something the reader can
 * fix. `error.code` is checked first because Firebase only guarantees the code,
 * not the wording.
 */
export const activationAuthMessage = (error) => {
  const code = error?.code || error?.message || "";
  if (code.includes("weak-password")) return "That password is too short. Use at least 6 characters.";
  if (code.includes("invalid-email")) return "The address on this invite is not a valid email. Ask your admin to check it.";
  if (code.includes("wrong-password") || code.includes("invalid-credential"))
    return "An account already exists for this address and that is not its password. Sign in instead, or reset it from the login page.";
  if (code.includes("too-many-requests")) return "Too many attempts. Wait a few minutes and try again.";
  if (code.includes("network-request-failed")) return "Could not reach the server. Check your connection and try again.";
  if (code.includes("operation-not-allowed")) return "Email sign-in is not enabled for this project.";
  return error?.message || "Could not create your account";
};

/**
 * Turn an activation link into a usable login.
 *
 * Two steps that fail differently, which is why the result says which one gave
 * way. Firebase creates (or signs in to) the account; the API then attaches
 * that Firebase uid to the User the admin pre-created. If step two fails the
 * Firebase account already exists, so a retry lands on `email-already-in-use`
 * and signs in instead — with the SAME password. Typing a different one on the
 * second attempt is a wrong-password error, which is why that case gets a
 * message of its own rather than the raw Firebase wording.
 */
export const activateAccount = async ({ email, password, userId, verificationToken }) => {
  let token;
  try {
    const created = await createUserWithEmailAndPassword(auth, email, password);
    token = await created.user.getIdToken();
  } catch (error) {
    if ((error?.code || error?.message || "").includes("email-already-in-use")) {
      try {
        const signedIn = await signInWithEmailAndPassword(auth, email, password);
        token = await signedIn.user.getIdToken();
      } catch (signInError) {
        return { success: false, stage: "account", message: activationAuthMessage(signInError) };
      }
    } else {
      return { success: false, stage: "account", message: activationAuthMessage(error) };
    }
  }

  // The API response is returned as-is on failure, `details` included, so the
  // page can point at a field instead of showing "Validation Failed".
  const res = await linkFirebase(token, userId, verificationToken);
  if (res?.success === false) return { ...res, stage: "link" };
  return { success: true, data: res?.data };
};

/**
 * Send a password reset link.
 *
 * This used to establish whether the address had an account by CREATING one
 * with the password "123456" and then deleting it, treating the resulting
 * email-already-in-use as proof the account existed. Two things wrong with
 * that. The delete is a second network call that can fail — a dropped
 * connection or a closed tab between the two left a live account on somebody
 * else's address with a password written in this file. And it answered "Email
 * not found" for addresses that had none, which turns the forgot-password form
 * into a way to test whether any given person has an account here.
 *
 * Firebase sends the mail without being told whether the account exists, so the
 * check was never needed. The answer is now the same either way: a genuinely
 * unknown address gets the same acknowledgement as a known one, and only errors
 * that are about the request rather than about the account are reported.
 */
export const sendResetPasswordEmail = async (email) => {
  if (!email || !email.trim()) {
    return { success: false, data: { message: "Enter your email address" } };
  }
  try {
    await sendPasswordResetEmail(auth, email.trim());
    return { success: true };
  } catch (error) {
    const code = error?.code || error?.message || "";
    // Firebase only raises this when email-enumeration protection is off. It is
    // deliberately reported as success: whether an address is registered is not
    // something an unauthenticated form should disclose.
    if (code.includes("user-not-found")) return { success: true };
    // A malformed address is about what they typed, not about who exists, so
    // saying so reveals nothing and saves them a silent failure.
    if (code.includes("invalid-email")) {
      return { success: false, data: { message: "That is not a valid email address" } };
    }
    if (code.includes("too-many-requests")) {
      return { success: false, data: { message: "Too many attempts. Wait a few minutes and try again." } };
    }
    if (code.includes("network-request-failed")) {
      return { success: false, data: { message: "Could not reach the server. Check your connection." } };
    }
    return { success: false, data: { message: "Could not send the reset email. Try again." } };
  }
};

export const signupWithGoogle = async () => {
  let response;
  let firebase_response;
  try {
    firebase_response = await signInWithPopup(auth, provider);
    const { user } = firebase_response;
    firebase_token = await idTokenOf(user);
    response = await registerUser(firebase_token);
    if (!response?.success) {
      // "User already exists" means this very Firebase account is already
      // registered — somebody signed up before, or came back and pressed Sign
      // up instead of Log in. They have just proved who they are with Google,
      // so sign them in rather than showing them a dialog asking them to do
      // the thing they were trying to do.
      //
      // Deliberately not the same for "Email already registered with another
      // user": that is a DIFFERENT Firebase account holding the address, and
      // signing in there would hand somebody else's account over. That case
      // keeps going to account recovery.
      if ((response?.message || "").toLowerCase() === "user already exists") {
        const existing = await loginUser(firebase_token);
        if (existing?.success) return { ...existing, firebase_token, created: false };
      }
      return { ...response, firebase_token };
    }
    // Log in with the very token that just registered the account. Without
    // this the caller has a created user and no session, and the only thing
    // it can do is send someone back to sign in with the account they
    // authenticated with a second earlier.
    const session = await loginUser(firebase_token);
    return { ...session, firebase_token, created: true };
  } catch (error) {
    // Returned, not logged and dropped. This used to `console.log` and fall off
    // the end, so the caller got undefined and the screen showed nothing at
    // all — which is exactly what happened the day the API was down: the
    // Google popup succeeded, the call to our server could not connect, and
    // the button simply did nothing. A sign-in that cannot say why it failed
    // sends people to support instead of to a retry.
    const code = error?.code || "";
    if (code.includes("popup-closed-by-user") || code.includes("cancelled-popup-request")) {
      // Not a failure: they changed their mind. Nothing to report.
      return { success: false, cancelled: true };
    }
    return {
      success: false,
      data: {
        message: code.includes("network-request-failed")
          ? "Could not reach the server. Check your connection and try again."
          : error?.message || "Could not sign in with Google",
      },
    };
  }
};

export const loginWithGoogle = async () => {
  let response;
  try {
    response = await signInWithPopup(auth, provider);
    const { user } = response;
    firebase_token = await idTokenOf(user);
    const accountRecoveryStatus = !!(
      sessionStorage.getItem("user-account-recovery") === "true"
    );
    if (accountRecoveryStatus)
      return { isFlagged: true, message: "recover-account" };
    response = await loginUser(firebase_token);
    return response;
  } catch (error) {
    // Returned, not logged and dropped. This used to `console.log` and fall off
    // the end, so the caller got undefined and the screen showed nothing at
    // all — which is exactly what happened the day the API was down: the
    // Google popup succeeded, the call to our server could not connect, and
    // the button simply did nothing. A sign-in that cannot say why it failed
    // sends people to support instead of to a retry.
    const code = error?.code || "";
    if (code.includes("popup-closed-by-user") || code.includes("cancelled-popup-request")) {
      // Not a failure: they changed their mind. Nothing to report.
      return { success: false, cancelled: true };
    }
    return {
      success: false,
      data: {
        message: code.includes("network-request-failed")
          ? "Could not reach the server. Check your connection and try again."
          : error?.message || "Could not sign in with Google",
      },
    };
  }
};

export const loginInWithEmailAndPassword = async (email, password) => {
  try {
    const user = await signInWithEmailAndPassword(auth, email, password);
    // Deliberately NOT checking auth.currentUser.emailVerified here.
    //
    // That check refused anyone whose Firebase account says unverified, and an
    // account created by the activation link always does:
    // createUserWithEmailAndPassword does not set the flag and nothing in the
    // invite flow sends a Firebase verification email. So every invited
    // employee set a password, was told "Email not verified", and was sent a
    // verification email nobody had mentioned — while the server was willing to
    // let them in all along.
    //
    // The server decides, because only it knows the other half: auth.js accepts
    // Firebase's flag OR our own emailVerifiedAt, which is recorded when an
    // invited employee produces the token from their activation link. It
    // answers 403 with code "email_not_verified" when neither holds, and the
    // login screen shows that.
    const token = await idTokenOf(user.user);
    const accountRecoveryStatus = !!(
      sessionStorage.getItem("user-account-recovery") === "true"
    );
    if (accountRecoveryStatus)
      return { isFlagged: true, message: "recover-account" };
    return await loginUser(token);
  } catch (error) {
    const code = error?.code || "";

    // An account created through Google has no password, so signing in with one
    // fails and Firebase says only "invalid credential" — which reads as "wrong
    // password" to somebody who never set one. Ask Firebase which methods the
    // address actually has and name the right button.
    //
    // fetchSignInMethodsForEmail returns nothing when email-enumeration
    // protection is on, which is a deliberate anti-harvesting measure, so this
    // is a nicety and never the only thing the message depends on.
    if (
      code.includes("invalid-credential") ||
      code.includes("wrong-password") ||
      code.includes("user-not-found")
    ) {
      let methods = [];
      try {
        methods = await fetchSignInMethodsForEmail(auth, email);
      } catch (e) {
        // enumeration protection, or offline — fall through to the general message
      }
      if (methods.length && !methods.includes("password")) {
        const provider = methods.includes("google.com") ? "Google" : methods[0];
        return {
          success: false,
          data: {
            message: `This account signs in with ${provider}. Use the ${provider} button instead.`,
            provider,
          },
        };
      }
      return {
        success: false,
        data: {
          message:
            "That email and password do not match. If you signed up with Google, use the Google button.",
        },
      };
    }

    if (code.includes("too-many-requests")) {
      return {
        success: false,
        data: { message: "Too many attempts. Wait a few minutes and try again." },
      };
    }

    console.log(error);
    return {
      success: false,
      data: { message: error?.message || "Could not sign in" },
    };
  }
};

export const getFromFirebaseToken = (firebaseToken, param) => {
  const token = firebaseToken.split(".")[1];
  const decodedToken = JSON.parse(atob(token));
  return decodedToken[param];
};

// ---------------------------------------------------------------------------
// Phone sign-in — WRITTEN BUT NOT IN USE.
//
// Nothing calls these yet, deliberately. Phone auth requires the Blaze plan
// (SMS is billed per message) and `pay-loom` is on Spark, so every send fails
// with BILLING_NOT_ENABLED no matter how the client is wired. The provider is
// enabled and the SMS region policy allows +91 — billing is the only gate left.
//
// Kept rather than deleted so turning it on is a UI job, not a rewrite. If you
// are the one turning it on: upgrade to Blaze, set a budget alert, and keep the
// SMS region allowlist to countries actually served (SMS pumping is why that
// allowlist exists).
//
// Two different jobs, and conflating them is the trap. `signInWithPhone` mints
// a NEW Firebase uid, so it only logs somebody in if that uid is the one stored
// on their User — otherwise the backend answers 404, correctly. Anyone who
// already signed up with email or Google must use `linkPhoneToCurrentUser`
// instead, which adds phone as a provider on the uid they already have.
//
// Firebase requires a reCAPTCHA before it will send an SMS. The verifier binds
// to a real DOM node and cannot be reused once its challenge is spent, so it is
// created per attempt and cleared on the way out; a stale verifier is the usual
// cause of "reCAPTCHA has already been rendered".
// ---------------------------------------------------------------------------

let recaptcha;

/** Tear down the current verifier so the next attempt can render a fresh one. */
export const clearRecaptcha = () => {
  try {
    recaptcha?.clear();
  } catch (error) {
    // Already torn down, or its container left the DOM. Nothing to recover.
  }
  recaptcha = undefined;
};

/**
 * The invisible reCAPTCHA Firebase requires before sending an SMS.
 * `containerId` must be an element that exists on the page at call time.
 */
const ensureRecaptcha = (containerId) => {
  if (recaptcha) return recaptcha;
  recaptcha = new RecaptchaVerifier(auth, containerId, {
    size: "invisible",
    // Expiry is not an error: the widget simply needs rebuilding before the
    // next send, and silently keeping the dead one makes the retry fail too.
    "expired-callback": clearRecaptcha,
  });
  return recaptcha;
};

/**
 * Send an SMS code. `phone` must be E.164 ("+919876543210") — Firebase rejects
 * anything else, and the app already stores numbers in that shape.
 *
 * Returns the confirmation handle; pass it to `confirmPhoneCode` with the code.
 */
export const startPhoneSignIn = async (phone, containerId = "recaptcha-container") => {
  try {
    const confirmation = await signInWithPhoneNumber(auth, phone, ensureRecaptcha(containerId));
    return { success: true, data: { confirmation } };
  } catch (error) {
    clearRecaptcha();
    return { success: false, data: { message: phoneAuthMessage(error) } };
  }
};

/** Finish a phone sign-in and exchange the Firebase token for an app session. */
export const confirmPhoneCode = async (confirmation, code) => {
  try {
    const credential = await confirmation.confirm(code);
    const token = await credential.user.getIdToken();
    clearRecaptcha();
    const accountRecoveryStatus = !!(
      sessionStorage.getItem("user-account-recovery") === "true"
    );
    if (accountRecoveryStatus) return { isFlagged: true, message: "recover-account" };
    return await loginUser(token);
  } catch (error) {
    return { success: false, data: { message: phoneAuthMessage(error) } };
  }
};

/**
 * Add phone as a sign-in method on the account already signed in.
 *
 * This is the path for existing email/Google users: it keeps their uid, so the
 * `firebaseId` on their User keeps resolving and nothing has to be re-linked.
 */
export const linkPhoneToCurrentUser = async (phone, containerId = "recaptcha-container") => {
  try {
    const current = auth.currentUser;
    if (!current) {
      return { success: false, data: { message: "Sign in before adding a phone number" } };
    }
    const confirmation = await linkWithPhoneNumber(current, phone, ensureRecaptcha(containerId));
    return { success: true, data: { confirmation } };
  } catch (error) {
    clearRecaptcha();
    return { success: false, data: { message: phoneAuthMessage(error) } };
  }
};

/** Confirm the code for a link, which returns no app session — the user has one. */
export const confirmPhoneLink = async (confirmation, code) => {
  try {
    await confirmation.confirm(code);
    clearRecaptcha();
    return { success: true };
  } catch (error) {
    return { success: false, data: { message: phoneAuthMessage(error) } };
  }
};

/**
 * Firebase phone errors in words a person can act on.
 *
 * The raw messages leak the error code in parentheses and say "quota" without
 * saying whose, so every one of these reads as a bug in the app rather than
 * something the user can fix by waiting or retyping.
 */
export const phoneAuthMessage = (error) => {
  const code = error?.code || "";
  if (code.includes("invalid-phone-number")) return "That is not a valid phone number. Include the country code, like +91.";
  if (code.includes("missing-phone-number")) return "Enter a phone number.";
  if (code.includes("invalid-verification-code")) return "That code is not right. Check the SMS and try again.";
  if (code.includes("code-expired")) return "That code has expired. Send a new one.";
  if (code.includes("too-many-requests")) return "Too many attempts from this number. Try again later.";
  if (code.includes("quota-exceeded")) return "The SMS limit for today has been reached. Try another sign-in method.";
  if (code.includes("credential-already-in-use") || code.includes("account-exists-with-different-credential"))
    return "That phone number is already on another account.";
  if (code.includes("provider-already-linked")) return "This account already has a phone number.";
  if (code.includes("captcha-check-failed")) return "The security check failed. Reload the page and try again.";
  if (code.includes("operation-not-allowed")) return "Phone sign-in is not enabled for this project.";
  return error?.message || "Could not verify that phone number";
};
